import { jsx as _jsx } from "react/jsx-runtime";
import { ApiError } from '@/lib/api/client';
import { createUploadLiveness, uploadWithStallGuard } from '@/lib/api/stall-guarded-upload';
import { completeImport, getImportStatus, requestImportUploadUrls, } from '@/lib/api/studies';
import { createUploadByteProgress } from '@/lib/api/upload-byte-progress';
import { extractImportTags, extractStudyDisplayTags, groupFilesByStudy, NON_DICOM_GROUP_KEY, UNPARSEABLE_GROUP_KEY, } from '@/lib/dicom/group-by-study';
import { useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useBeforeUnloadGuard } from './use-before-unload-guard';
import { toast } from './use-toast';
const BATCH_SIZE = 20;
const CONCURRENCY = 3;
// Global cap on concurrent GCS PUTs across the WHOLE import run (every study,
// every batch). Decouples upload throughput from BATCH_SIZE: a single large
// study is only one `worker`, so without this a study's uploads would never
// exceed BATCH_SIZE in flight. Shared via one pLimit gate created once per run.
const UPLOAD_CONCURRENCY = 50;
// Per-study cap on how many batch lifecycles (extract tags -> sign -> PUTs ->
// complete) run concurrently. Lets a few batches' sign/complete round-trips
// overlap with PUTs from other batches — all sharing the global putLimit pool
// above — so the pipe stays full across batch boundaries instead of draining
// to zero between batches (the old sequential-batch behavior).
const SIGN_AHEAD_BATCHES = 3;
// Floor for a batch's sign-call timeout. The PUTs carry no wall-clock deadline
// (see uploadWithStallGuard), and the async parse phase (complete + poll) is
// bounded by STATUS_POLL_CAP_MS — see runStudyEngine's tail.
const BATCH_TIMEOUT_MS = 30_000;
// Floor throughput assumed when scaling the sign timeout with a batch's bytes.
const MIN_UPLOAD_BYTES_PER_MS = 1024; // ~1 MB/s floor
const MAX_RETRIES = 1;
/**
 * Timeout for a batch's sign call, scaled by the batch's total bytes. Small
 * batches keep the BATCH_TIMEOUT_MS floor; large ones get proportionally
 * longer (1 MB/s floor → 700 MB ≈ 700 s, 1 GiB ≈ 1075 s). NOT used for the
 * PUTs: any fixed throughput floor fails a slow uplink that many concurrent
 * PUTs are sharing, so those are bounded by stalling instead.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function computeUploadTimeoutMs(batchBytes) {
    return Math.max(BATCH_TIMEOUT_MS, Math.ceil(batchBytes / MIN_UPLOAD_BYTES_PER_MS));
}
// Async-parse polling: how often to ask /study/import/status, and the floor for
// the per-study ceiling before we stop waiting and surface "still processing".
const STATUS_POLL_INTERVAL_MS = 2_000;
const STATUS_POLL_CAP_MS = 5 * 60_000;
// getImportStatus sends one repeated `ids` query param per upload; a whole study
// can be thousands of ids, so each poll round queries in URL-safe chunks.
const STATUS_POLL_CHUNK = 100;
/**
 * Ceiling for the per-study settle poll, scaled by how many uploads it watches.
 * The 5-minute floor covers small imports; a multi-thousand-file study gets
 * proportionally longer (250 ms/file ≈ 9.4 min for 2 255 files) so a backend
 * draining at a modest rate isn't misreported as "still processing".
 */
// eslint-disable-next-line react-refresh/only-export-components
export function computeSettleCapMs(uploadCount) {
    return Math.max(STATUS_POLL_CAP_MS, uploadCount * 250);
}
// The worker has finished with an upload once it reaches one of these states.
const TERMINAL_IMPORT_STATES = new Set(['finalized', 'failed', 'expired']);
// Recent imports carry patient-facing fields (patient name / description), so they
// must not outlive the session or surface for the next user on a shared workstation.
// Persist in sessionStorage (cleared on browser/tab close) and wipe on logout via
// clearPersistedRecentImports() — wired into use-auth's logout().
const RECENT_IMPORTS_KEY = 'newlumen_recent_imports';
const MAX_RECENT_IMPORTS = 15;
// Provider-registered callbacks that drop the in-memory list when logout fires.
// ImportProvider wraps every route (including /login), so it stays mounted across a
// client-side logout — clearing storage alone wouldn't clear the mounted state.
const recentImportsResetListeners = new Set();
function loadRecentImports() {
    try {
        const raw = sessionStorage.getItem(RECENT_IMPORTS_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
function saveRecentImports(list) {
    try {
        sessionStorage.setItem(RECENT_IMPORTS_KEY, JSON.stringify(list));
    }
    catch {
        // Non-fatal (private mode / quota).
    }
}
/**
 * Clears persisted recent imports — storage and any mounted provider's in-memory
 * list. Called from the auth logout flow (shared-machine hygiene) so one user's
 * imported patient info never surfaces for the next user in the same browser.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function clearPersistedRecentImports() {
    try {
        sessionStorage.removeItem(RECENT_IMPORTS_KEY);
    }
    catch {
        // Non-fatal.
    }
    recentImportsResetListeners.forEach((reset) => reset());
}
const emptyResult = {
    totalFiles: 0,
    successCount: 0,
    failedCount: 0,
    warningCount: 0,
    results: [],
};
const initialState = {
    status: 'idle',
    totalFiles: 0,
    uploadedFiles: 0,
    completedFiles: 0,
    totalBytes: 0,
    uploadedBytes: 0,
    totalStudies: 0,
    completedStudies: 0,
    totalBatches: 0,
    completedBatches: 0,
    result: emptyResult,
    error: null,
};
const ImportContext = createContext(null);
export function ImportProvider({ children }) {
    const [state, setState] = useState(initialState);
    const [recentImports, setRecentImports] = useState(() => loadRecentImports());
    const abortControllerRef = useRef(null);
    const queryClient = useQueryClient();
    const recordRecentImports = useCallback((entries) => {
        if (entries.length === 0)
            return;
        setRecentImports((prev) => {
            // Newest first; dedupe by studyUid (a re-import updates its entry).
            const seen = new Set();
            const merged = [];
            for (const item of [...entries, ...prev]) {
                if (seen.has(item.studyUid))
                    continue;
                seen.add(item.studyUid);
                merged.push(item);
            }
            const trimmed = merged.slice(0, MAX_RECENT_IMPORTS);
            saveRecentImports(trimmed);
            return trimmed;
        });
    }, []);
    const clearRecentImports = useCallback(() => {
        setRecentImports([]);
        saveRecentImports([]);
    }, []);
    // Drop the in-memory list when logout wipes persisted imports (shared-machine hygiene).
    useEffect(() => {
        const reset = () => setRecentImports([]);
        recentImportsResetListeners.add(reset);
        return () => {
            recentImportsResetListeners.delete(reset);
        };
    }, []);
    // The uploads run in this tab: closing or reloading it while any file is still
    // going up (or not yet handed to the backend) loses that file. Once every file
    // is dispositioned the backend parses on its own, so leaving is harmless.
    useBeforeUnloadGuard(isUploadInFlight(state));
    const startImport = useCallback((files, clinicId, options) => {
        if (state.status === 'importing')
            return;
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setState({
            ...initialState,
            status: 'importing',
            totalFiles: files.length,
            totalBytes: files.reduce((sum, f) => sum + f.size, 0),
        });
        // One session id per Import action groups all its non-DICOM files into a
        // single synthetic study server-side, even across batched sign calls.
        const importSessionId = crypto.randomUUID();
        runStudyEngine(files, clinicId, abortController, setState, queryClient, recordRecentImports, options, importSessionId);
    }, [state.status, queryClient, recordRecentImports]);
    const cancelImport = useCallback(() => {
        if (state.status !== 'importing')
            return;
        abortControllerRef.current?.abort();
    }, [state.status]);
    const dismissImport = useCallback(() => {
        setState(initialState);
    }, []);
    return (_jsx(ImportContext.Provider, { value: {
            state,
            startImport,
            cancelImport,
            dismissImport,
            recentImports,
            clearRecentImports,
        }, children: children }));
}
/**
 * True while any of the run's files is still uploading or waiting to be handed
 * to the backend — the window in which leaving the page loses work.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isUploadInFlight(state) {
    return state.status === 'importing' && state.uploadedFiles < state.totalFiles;
}
// eslint-disable-next-line react-refresh/only-export-components
export function useImport() {
    const context = useContext(ImportContext);
    if (!context) {
        throw new Error('useImport must be used within an ImportProvider');
    }
    return context;
}
function planStudies(groups) {
    return groups.map((group) => {
        const batches = [];
        for (let i = 0; i < group.files.length; i += BATCH_SIZE) {
            batches.push(group.files.slice(i, i + BATCH_SIZE));
        }
        return { key: group.key, batches, totalFiles: group.files.length };
    });
}
/**
 * Aggregation + toast tail for the import engine. Mutates state and fires
 * toast; also invalidates the studies query if any files succeeded.
 */
async function finishImport(aggregatedResult, failedStudies, lastError, wasCancelled, setState, queryClient, clinicId, displayByUid, recordRecentImports) {
    if (wasCancelled) {
        console.log(`[Import] Cancelled — ${aggregatedResult.successCount}/${aggregatedResult.totalFiles} succeeded`);
        setState((prev) => ({
            ...prev,
            status: 'cancelled',
            result: { ...aggregatedResult },
        }));
        toast({
            title: i18n.t('import.toast.cancelled'),
            description: i18n.t('import.toast.cancelledDesc', {
                count: aggregatedResult.successCount,
            }),
        });
    }
    else if (aggregatedResult.failedCount > 0 && aggregatedResult.successCount === 0) {
        setState((prev) => ({
            ...prev,
            status: 'failed',
            error: lastError,
            result: { ...aggregatedResult },
        }));
        toast({
            title: i18n.t('import.toast.failed'),
            description: i18n.t('import.toast.failedDesc', {
                error: lastError ?? i18n.t('error.unexpected'),
            }),
            variant: 'destructive',
        });
    }
    else {
        setState((prev) => ({
            ...prev,
            status: 'completed',
            result: { ...aggregatedResult },
        }));
        if (failedStudies > 0) {
            toast({
                title: i18n.t('import.toast.partial'),
                description: `${i18n.t('import.toast.completeDesc', { count: aggregatedResult.successCount, total: aggregatedResult.totalFiles })}, ${i18n.t('import.toast.partialStudiesFailed', { count: failedStudies })}`,
                variant: 'destructive',
            });
        }
        else {
            toast({
                title: i18n.t('import.toast.complete'),
                description: i18n.t('import.toast.completeDesc', {
                    count: aggregatedResult.successCount,
                    total: aggregatedResult.totalFiles,
                }),
            });
        }
    }
    if (aggregatedResult.successCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['studies'] });
        // Record one "recent import" per study that landed at least one file, so the
        // Import menu can offer a shortcut to each newly-imported patient's study card.
        const fileCountByUid = new Map();
        for (const r of aggregatedResult.results) {
            if (r.success && r.studyInstanceUid) {
                fileCountByUid.set(r.studyInstanceUid, (fileCountByUid.get(r.studyInstanceUid) ?? 0) + 1);
            }
        }
        const completedAt = Date.now();
        const entries = [...fileCountByUid.entries()].map(([studyUid, fileCount]) => {
            const tags = displayByUid.get(studyUid);
            return {
                studyUid,
                fileCount,
                clinicId,
                completedAt,
                patientName: tags?.patientName ?? undefined,
                description: tags?.description ?? undefined,
                modality: tags?.modality ?? undefined,
            };
        });
        recordRecentImports(entries);
    }
}
/**
 * Maps an upload/batch failure to a user-facing message. A status-0 ApiError
 * (uploadToSignedUrl normalizes fetch/XHR network failures to this) or a raw
 * fetch TypeError means the browser never reached GCS — almost always a
 * CORS/connectivity problem — so we surface an actionable network message
 * instead of the browser's opaque "NetworkError"/"Failed to fetch". Other
 * ApiErrors carry a meaningful server message and pass through unchanged.
 */
function describeUploadError(error) {
    if (error instanceof TypeError)
        return i18n.t('import.toast.networkError');
    if (error instanceof ApiError) {
        return error.status === 0 ? i18n.t('import.toast.networkError') : error.message;
    }
    return error instanceof Error ? error.message : i18n.t('error.unexpected');
}
/**
 * Resolves after `ms`, or rejects with an AbortError the moment `signal` fires —
 * so a cancel mid-wait doesn't block for the full poll interval.
 */
function abortableDelay(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * Settle poll that runs CONCURRENTLY with a study's upload stream: every
 * STATUS_POLL_INTERVAL_MS it snapshots the ids completed so far, queries the
 * not-yet-terminal ones in URL-safe chunks, and reports the running settled
 * count via `onProgress` — so the progress bar tracks real finalization while
 * later batches are still uploading, instead of sitting at zero until the
 * stream ends. Exits when the stream is done AND every id is terminal, or when
 * the scaled cap (computeSettleCapMs, armed once the stream finishes) elapses.
 * A transient status failure logs and retries next round — the bytes uploaded
 * and the backend keeps parsing regardless; the cap bounds the loop. Honors
 * the abort signal (a cancel stops the loop promptly and rejects).
 *
 * NOTE: this assumes the out-of-process ImportWorker is running to move rows to
 * `finalized`/`failed`; if it isn't (a deploy/dev concern), uploads stay queued
 * and the poll exhausts the cap → "still processing".
 */
async function pollImportUntilSettled(clinicId, source, signal, onProgress) {
    const latest = new Map();
    const isTerminal = (id) => {
        const st = latest.get(id);
        return st != null && TERMINAL_IMPORT_STATES.has(st.state);
    };
    let deadline = null;
    while (true) {
        if (signal.aborted)
            throw new DOMException('Aborted', 'AbortError');
        const snapshot = [...source.ids()];
        const pending = snapshot.filter((id) => !isTerminal(id));
        try {
            // Chunks in parallel (HTTP/2 multiplexes them) so a round with thousands
            // of pending ids stays ~one RTT instead of stretching the poll cadence.
            const chunks = [];
            for (let i = 0; i < pending.length; i += STATUS_POLL_CHUNK) {
                chunks.push(pending.slice(i, i + STATUS_POLL_CHUNK));
            }
            const rounds = await Promise.all(chunks.map((chunk) => getImportStatus(clinicId, chunk, signal)));
            for (const statuses of rounds)
                for (const s of statuses)
                    latest.set(s.uploadId, s);
        }
        catch (error) {
            if (signal.aborted)
                throw error;
            console.warn('[Import/Direct] status poll round failed; retrying next round:', error);
        }
        onProgress(snapshot.filter(isTerminal).length);
        const streamDone = source.isStreamDone();
        if (streamDone) {
            const allIds = source.ids();
            if (allIds.length === snapshot.length && allIds.every(isTerminal))
                break;
            deadline ??= Date.now() + computeSettleCapMs(allIds.length);
            if (Date.now() >= deadline)
                break;
            await abortableDelay(STATUS_POLL_INTERVAL_MS, signal);
        }
        else {
            // Wake early when the stream finishes so the final settle check doesn't
            // idle out a full poll interval. The losing delay's abort rejection is
            // swallowed — the loop's own abort check surfaces cancellation.
            const delay = abortableDelay(STATUS_POLL_INTERVAL_MS, signal);
            delay.catch(() => { });
            await Promise.race([delay, source.streamDone]);
        }
    }
    return latest;
}
/**
 * Maps the polled per-upload states to per-file import results: finalized →
 * success; failed → failure carrying the worker's error text; expired → failure;
 * anything still non-terminal at the cap (pending/queued/processing, or never
 * reported) → a "still processing" warning, since the bytes did upload — the
 * worker just hasn't finished parsing them yet.
 */
function buildParseResults(uploadIds, uploadIdToFileName, latest) {
    const results = [];
    let successCount = 0;
    let failedCount = 0;
    let warningCount = 0;
    for (const id of uploadIds) {
        const fileName = uploadIdToFileName.get(id) ?? id;
        const status = latest.get(id);
        if (status?.state === 'finalized') {
            successCount++;
            results.push({ fileName, success: true });
        }
        else if (status?.state === 'failed') {
            failedCount++;
            results.push({
                fileName,
                success: false,
                error: status.error ?? i18n.t('import.toast.parseFailed'),
            });
        }
        else if (status?.state === 'expired') {
            failedCount++;
            results.push({ fileName, success: false, error: i18n.t('import.toast.uploadExpired') });
        }
        else {
            warningCount++;
            results.push({ fileName, success: false, warning: i18n.t('import.toast.stillProcessing') });
        }
    }
    return { results, successCount, failedCount, warningCount };
}
/**
 * Concurrency gate: `pLimit(n)` returns a `limit` function such that
 * `limit(() => task())` never runs more than `n` tasks at once — extra calls
 * queue FIFO and start as active tasks settle (resolve or reject). Hand-rolled
 * instead of a dependency (e.g. p-limit) since the need is this small; caps
 * concurrent GCS PUTs globally (UPLOAD_CONCURRENCY) and concurrent batch
 * lifecycles per study (SIGN_AHEAD_BATCHES).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function pLimit(n) {
    if (!Number.isInteger(n) || n < 1) {
        throw new RangeError('pLimit: concurrency must be a positive integer');
    }
    let active = 0;
    // FIFO queue drained via a head-index pointer rather than Array.shift (which
    // is O(n)): a large study enqueues hundreds of batch tasks into batchLimit at
    // once, so repeated shift would be O(n²). We advance `head` on dequeue and
    // compact the dead prefix only once it dominates, keeping enqueue+dequeue
    // amortized O(1) without unbounded array growth.
    const queue = [];
    let head = 0;
    const runNext = () => {
        active--;
        if (head < queue.length) {
            const fn = queue[head];
            queue[head] = undefined;
            head++;
            // Compact once the dead prefix is both large and at least half the array,
            // so amortized cost stays O(1) and the backing array can't grow forever.
            if (head > 64 && head * 2 >= queue.length) {
                queue.splice(0, head);
                head = 0;
            }
            fn?.();
        }
    };
    return function limit(task) {
        return new Promise((resolve, reject) => {
            const run = () => {
                active++;
                // A task that throws SYNCHRONOUSLY (before returning its promise) would
                // otherwise escape before `.then` attaches, leaking the slot. Both live
                // call sites pass async fns so this can't happen today, but the gate is
                // exported/reusable — guard it so a sync throw still releases the slot.
                try {
                    task().then((value) => {
                        runNext();
                        resolve(value);
                    }, (error) => {
                        runNext();
                        reject(error);
                    });
                }
                catch (error) {
                    runNext();
                    reject(error);
                }
            };
            if (active < n)
                run();
            else
                queue.push(run);
        });
    };
}
/**
 * Direct-to-GCS import engine — the sole import path. Groups files by study,
 * splits each study into BATCH_SIZE batches, and drives CONCURRENCY workers
 * (one study per worker). Per study it runs two phases:
 *
 * Phase 1 — upload stream. Per batch: extract DICOM tags, sign via
 * requestImportUploadUrls, PUT each non-skipped file, then completeImport —
 * WITHOUT waiting for the backend to parse. Completing a batch dispatches its
 * uploads to the import worker immediately, so parsing overlaps the remaining
 * uploads instead of serializing behind a per-batch settle wait (which made a
 * 2 255-file study take ~9 min of mostly idle backend: ~5 s per 20-file batch
 * × 113 batches). A sign/PUT/complete failure aborts the study's remaining
 * (not-yet-started) batches; a batch already in flight when that happens may
 * still finish on its own.
 *
 * Upload concurrency is decoupled from BATCH_SIZE by two pLimit gates: a
 * per-study `batchLimit` (SIGN_AHEAD_BATCHES) lets a few batches' full
 * lifecycles (extract -> sign -> PUTs -> complete) run at once instead of
 * strictly sequentially, and a single GLOBAL `putLimit` (UPLOAD_CONCURRENCY),
 * shared by every study/worker in the run, caps concurrent GCS PUTs across all
 * of them. Together these keep PUTs flowing across batch (and study)
 * boundaries instead of the pipe draining to zero between batches. A PUT has
 * no wall-clock deadline — those PUTs all share one uplink, so any per-file
 * throughput floor fails a slow connection that is in fact making progress.
 * It is abandoned only when the whole run stops moving bytes
 * (uploadWithStallGuard). A batch retry re-signs and re-sends only the files
 * that have not landed, and files that did land are completed even when
 * siblings in the same batch never make it.
 *
 * Phase 2 — settle. A pollImportUntilSettled loop runs CONCURRENTLY with the
 * stream (watching the id list grow), advancing the progress bar as the
 * backend finalizes files behind the uploads; the study reconciles per-file
 * results from the polled terminal states once both finish, with skipped files
 * appended as warnings so totals reconcile. Wall time becomes
 * max(upload stream, backend drain) + one poll tick instead of their sum.
 *
 * On abort: stops signing/uploading/polling further files and exits. No backend
 * cancel call — orphaned objects are swept by the StorageCleanup background job.
 */
async function runStudyEngine(files, clinicId, abortController, setState, queryClient, recordRecentImports, options, importSessionId) {
    const sessionId = importSessionId ?? crypto.randomUUID();
    const aggregatedResult = {
        totalFiles: 0,
        successCount: 0,
        failedCount: 0,
        warningCount: 0,
        results: [],
    };
    let groups;
    try {
        groups = await groupFilesByStudy(files);
    }
    catch (error) {
        const message = error instanceof Error ? error.message : i18n.t('import.toast.scanFailed');
        console.error('[Import/Direct] Pre-flight grouping failed:', error);
        setState((prev) => ({
            ...prev,
            status: 'failed',
            error: message,
            result: { ...aggregatedResult },
        }));
        toast({
            title: i18n.t('import.toast.failed'),
            description: message,
            variant: 'destructive',
        });
        return;
    }
    // Best-effort capture of patient/description/modality per DICOM study group for
    // the "recent imports" menu. One extra header parse per study; failures are
    // tolerated (the menu falls back to a neutral label).
    const displayByUid = new Map();
    await Promise.all(groups.map(async (g) => {
        if (g.key === NON_DICOM_GROUP_KEY || g.key === UNPARSEABLE_GROUP_KEY)
            return;
        if (g.files.length === 0)
            return;
        displayByUid.set(g.key, await extractStudyDisplayTags(g.files[0]));
    }));
    const studies = planStudies(groups);
    const totalBatches = studies.reduce((n, s) => n + s.batches.length, 0);
    setState((prev) => ({
        ...prev,
        totalStudies: studies.length,
        totalBatches,
    }));
    let queueIndex = 0;
    let completedStudies = 0;
    let failedStudies = 0;
    let completedBatches = 0;
    let uploadedFiles = 0;
    let completedFiles = 0;
    let lastError = null;
    // Shared across every study/worker in this run — the single global cap on
    // concurrent GCS PUTs (see UPLOAD_CONCURRENCY).
    const putLimit = pLimit(UPLOAD_CONCURRENCY);
    // Also shared across the whole run: every PUT feeds one liveness clock, so
    // an upload is only given up on when nothing in the run is moving bytes.
    const uploadLiveness = createUploadLiveness();
    // Byte-level progress across the whole run (see createUploadByteProgress).
    const byteProgress = createUploadByteProgress((uploadedBytes) => setState((prev) => ({ ...prev, uploadedBytes })));
    console.log(`[Import/Direct] Starting — ${files.length} files in ${studies.length} studies, ${totalBatches} batches, ${Math.min(CONCURRENCY, studies.length)} workers`);
    async function worker(workerId) {
        const { signal } = abortController;
        while (true) {
            if (signal.aborted)
                return;
            const studyIdx = queueIndex++;
            if (studyIdx >= studies.length)
                break;
            const study = studies[studyIdx];
            // Everything the study uploads accumulates here; the settle poll below
            // watches it grow and the tail of the loop reconciles the final states.
            const studyUploadedIds = [];
            const studyIdToFileName = new Map();
            const studySkipped = [];
            let studyAborted = false;
            // Launch the settle poll CONCURRENTLY with the upload stream: it advances
            // completedFiles as the backend finalizes files batches behind the
            // uploads, so the progress bar moves in near-real-time instead of sitting
            // at zero until the stream ends. Progress lands as increments on the
            // shared counter (CONCURRENCY study workers interleave).
            let uploadStreamDone = false;
            let wakeSettle;
            const streamDoneSignal = new Promise((resolve) => (wakeSettle = resolve));
            let studySettled = 0;
            const settlePromise = pollImportUntilSettled(clinicId, {
                ids: () => studyUploadedIds,
                isStreamDone: () => uploadStreamDone,
                streamDone: streamDoneSignal,
            }, signal, (settledCount) => {
                completedFiles += settledCount - studySettled;
                studySettled = settledCount;
                setState((prev) => ({ ...prev, completedFiles }));
            });
            // The worker may return early on abort while the poll is mid-flight; its
            // abort rejection is observed at the await below or swallowed here.
            settlePromise.catch(() => { });
            // Each batch's full lifecycle (extract tags -> sign -> PUTs -> complete)
            // runs as its own task, gated by a per-study batchLimit so up to
            // SIGN_AHEAD_BATCHES run at once — their sign/complete round-trips
            // overlap with PUTs from other batches, all sharing the GLOBAL putLimit
            // pool, so the pipe stays full across batch boundaries.
            async function processBatch(batchIdx, batch) {
                if (signal.aborted)
                    return;
                if (studyAborted) {
                    // Not-yet-started batch after a sibling batch failed — skip without
                    // attempting. A batch already in flight when the failure landed is
                    // unaffected by this check (it may still finish on its own).
                    const skippedResults = batch.map((f) => ({
                        fileName: f.name,
                        success: false,
                        error: 'Skipped after study failure',
                    }));
                    aggregatedResult.totalFiles += batch.length;
                    aggregatedResult.failedCount += batch.length;
                    aggregatedResult.results.push(...skippedResults);
                    for (const f of batch)
                        byteProgress.settle(f);
                    completedBatches++;
                    uploadedFiles += batch.length;
                    completedFiles += batch.length;
                    setState((prev) => ({
                        ...prev,
                        completedBatches,
                        uploadedFiles,
                        completedFiles,
                        result: { ...aggregatedResult },
                    }));
                    return;
                }
                let attemptError = null;
                // Sign + upload phase (retryable). What it leaves behind accumulates
                // ACROSS attempts: `uploadedIds` (objects now in GCS, keyed back to
                // file names via `uploadIdToFileName`), `skippedInBatch` (files /sign
                // told us to skip), and `pendingRefs` — the batch indices still to
                // upload. A retry re-signs and re-sends only those, so a file that
                // already landed is never uploaded twice.
                const uploadedIds = [];
                const uploadIdToFileName = new Map();
                const skippedInBatch = [];
                let pendingRefs = batch.map((_, idx) => idx);
                // Scale the sign timeout by the bytes in this batch so a large file
                // (or many files on a slow/contended link) isn't aborted mid-sign.
                const batchBytes = batch.reduce((sum, f) => sum + f.size, 0);
                let attemptLabel = '';
                for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                    if (signal.aborted)
                        return;
                    await new Promise((r) => setTimeout(r, 0));
                    const timeoutSignal = AbortSignal.timeout(computeUploadTimeoutMs(batchBytes));
                    const combinedSignal = AbortSignal.any([signal, timeoutSignal]);
                    attemptLabel = attempt > 0 ? ` (retry ${attempt}/${MAX_RETRIES})` : '';
                    console.log(`[Import/Direct] Worker ${workerId} — study ${studyIdx + 1}/${studies.length} batch ${batchIdx + 1}/${study.batches.length} starting${attemptLabel} (${pendingRefs.length} files)`);
                    try {
                        // Step 1: extract DICOM tags for each file still to upload.
                        // clientRef is the batch index as a string so the Map lookup in step 3
                        // is unambiguous even when two files share the same name.
                        const batchFileMap = new Map(batch.map((f, idx) => [String(idx), f]));
                        // All non-DICOM files of one import live in a single group; give each a
                        // 1-based index across the whole group (batches run sequentially) so the
                        // backend assigns stable instance numbers within the one synthetic study.
                        const isNonDicomGroup = study.key === NON_DICOM_GROUP_KEY;
                        const descriptors = await Promise.all(pendingRefs.map(async (idx) => {
                            const f = batch[idx];
                            const tags = await extractImportTags(f);
                            return {
                                clientRef: String(idx),
                                fileName: f.name,
                                sizeBytes: f.size,
                                isDicom: tags.isDicom,
                                sopInstanceUid: tags.sopUid,
                                nonDicomIndex: isNonDicomGroup ? batchIdx * BATCH_SIZE + idx + 1 : undefined,
                            };
                        }));
                        if (signal.aborted)
                            return;
                        // Step 2: sign the batch (timeout-guarded). Non-DICOM naming rides
                        // along only when the batch actually carries non-DICOM files — the
                        // backend applies it to those rows' synthetic study/DICOM wrapper.
                        const hasNonDicom = descriptors.some((d) => !d.isDicom);
                        const signed = await requestImportUploadUrls(clinicId, descriptors, combinedSignal, hasNonDicom ? options?.nonDicomMetadata : undefined, sessionId);
                        if (signal.aborted)
                            return;
                        // Step 3: upload each non-skipped file. Each PUT is gated by the
                        // GLOBAL putLimit — capping total concurrent GCS PUTs across every
                        // study/batch in this run — rather than only by this batch's
                        // Promise.all, so PUTs from other batches (this study's or
                        // another study's) share the same pool and keep it full across
                        // batch boundaries. A failed PUT is recorded rather than thrown, so
                        // the batch always waits for every sibling to settle: the files
                        // that land are kept, and a retry never overlaps PUTs still in
                        // flight from the attempt before it.
                        const failedRefs = [];
                        let firstUploadError = null;
                        await Promise.all(signed.map(async (signedFile) => {
                            const fileName = batchFileMap.get(signedFile.clientRef)?.name ?? signedFile.clientRef;
                            if (signedFile.skipped || !signedFile.uploadUrl || !signedFile.uploadId) {
                                // Skipped by /sign (oversize, duplicate, unsupported) — a warning, not
                                // a failure, so it matches how skippedInBatch is counted (warningCount).
                                const skippedFile = batchFileMap.get(signedFile.clientRef);
                                if (skippedFile)
                                    byteProgress.settle(skippedFile);
                                skippedInBatch.push({
                                    fileName,
                                    success: false,
                                    warning: signedFile.skipReason ?? i18n.t('import.toast.skipped'),
                                });
                                return;
                            }
                            const fileForUpload = batchFileMap.get(signedFile.clientRef);
                            // Narrow to locals so the non-null check survives into the
                            // putLimit closure below (TS narrowing on a property access
                            // doesn't cross a function boundary; a const does).
                            const uploadUrl = signedFile.uploadUrl;
                            const uploadId = signedFile.uploadId;
                            await putLimit(async () => {
                                if (signal.aborted)
                                    return;
                                // No wall-clock deadline: on a slow uplink shared by every
                                // PUT in flight, a file takes as long as it takes. Each
                                // attempt is abandoned only once the whole run has stopped
                                // moving bytes (see uploadWithStallGuard).
                                let uploadAttemptError = null;
                                for (let ua = 0; ua <= MAX_RETRIES; ua++) {
                                    if (signal.aborted)
                                        return;
                                    try {
                                        await uploadWithStallGuard(uploadUrl, fileForUpload, signal, uploadLiveness, (loaded) => byteProgress.report(fileForUpload, loaded));
                                        byteProgress.settle(fileForUpload);
                                        uploadAttemptError = null;
                                        break;
                                    }
                                    catch (err) {
                                        if (signal.aborted)
                                            return;
                                        uploadAttemptError = err;
                                        console.warn(`[Import/Direct] Worker ${workerId} — upload failed for ${signedFile.clientRef} (attempt ${ua + 1}):`, err);
                                    }
                                }
                                if (uploadAttemptError) {
                                    failedRefs.push(Number(signedFile.clientRef));
                                    firstUploadError ??= uploadAttemptError;
                                    return;
                                }
                                uploadedIds.push(uploadId);
                                uploadIdToFileName.set(uploadId, fileName);
                            });
                        }));
                        if (signal.aborted)
                            return;
                        pendingRefs = failedRefs;
                        if (failedRefs.length === 0) {
                            attemptError = null;
                            break;
                        }
                        throw firstUploadError;
                    }
                    catch (error) {
                        if (signal.aborted)
                            return;
                        attemptError = error;
                        const isTimeout = error instanceof DOMException && error.name === 'TimeoutError';
                        console.warn(`[Import/Direct] Worker ${workerId} — study ${studyIdx + 1} batch ${batchIdx + 1} ${isTimeout ? 'timed out' : 'failed'}${attemptLabel}:`, error);
                    }
                }
                // Complete phase — hand whatever landed to the backend and move on,
                // even when some of the batch's files never made it: their siblings
                // are in GCS and should be imported rather than orphaned.
                // Dispatch/parsing starts server-side immediately; the settle wait
                // happens once per study in phase 2. Not retried: the bytes are
                // already in GCS, so re-running sign+PUT would only orphan objects
                // (swept by StorageCleanup).
                let failedFiles = pendingRefs.map((idx) => batch[idx]);
                try {
                    if (uploadedIds.length > 0) {
                        await completeImport(clinicId, uploadedIds, signal);
                        if (signal.aborted)
                            return;
                        studyUploadedIds.push(...uploadedIds);
                        for (const [id, name] of uploadIdToFileName)
                            studyIdToFileName.set(id, name);
                    }
                    studySkipped.push(...skippedInBatch);
                    // The upload layer of the progress bar: these files are now
                    // dispositioned (bytes handed to the backend, or skipped by /sign).
                    uploadedFiles += uploadedIds.length + skippedInBatch.length;
                    if (failedFiles.length === 0) {
                        completedBatches++;
                        setState((prev) => ({ ...prev, completedBatches, uploadedFiles }));
                        return;
                    }
                }
                catch (error) {
                    if (signal.aborted)
                        return;
                    attemptError = error;
                    // Nothing was handed off, so the whole batch is lost.
                    failedFiles = batch;
                    console.warn(`[Import/Direct] Worker ${workerId} — study ${studyIdx + 1} batch ${batchIdx + 1} complete failed:`, error);
                }
                // Sign/PUT/complete failed for some or all of this batch — abort the
                // study's remaining not-yet-started batches (they'd almost certainly
                // fail the same way).
                studyAborted = true;
                lastError = describeUploadError(attemptError);
                const failedResults = failedFiles.map((f) => ({
                    fileName: f.name,
                    success: false,
                    error: 'Upload failed',
                }));
                aggregatedResult.totalFiles += failedFiles.length;
                aggregatedResult.failedCount += failedFiles.length;
                aggregatedResult.results.push(...failedResults);
                for (const f of failedFiles)
                    byteProgress.settle(f);
                completedBatches++;
                uploadedFiles += failedFiles.length;
                completedFiles += failedFiles.length;
                setState((prev) => ({
                    ...prev,
                    completedBatches,
                    uploadedFiles,
                    completedFiles,
                    result: { ...aggregatedResult },
                }));
            }
            const batchLimit = pLimit(SIGN_AHEAD_BATCHES);
            await Promise.all(study.batches.map((batch, batchIdx) => batchLimit(() => processBatch(batchIdx, batch))));
            if (signal.aborted)
                return;
            // Upload stream finished (or aborted) — wake the settle poll for its
            // final rounds and reconcile the study's terminal states.
            uploadStreamDone = true;
            wakeSettle();
            let studyParseFailed = false;
            try {
                const latest = await settlePromise;
                if (signal.aborted)
                    return;
                const parsed = buildParseResults(studyUploadedIds, studyIdToFileName, latest);
                aggregatedResult.totalFiles += parsed.results.length + studySkipped.length;
                aggregatedResult.successCount += parsed.successCount;
                aggregatedResult.failedCount += parsed.failedCount;
                aggregatedResult.warningCount += parsed.warningCount + studySkipped.length;
                aggregatedResult.results.push(...parsed.results, ...studySkipped);
                // A study whose uploads produced zero successes and at least one real
                // failure counts as failed (warnings-only — e.g. all-duplicates — does not).
                if (parsed.successCount === 0 && parsed.failedCount > 0) {
                    studyParseFailed = true;
                    lastError =
                        parsed.results.find((r) => r.error != null)?.error ?? i18n.t('error.unexpected');
                    console.error(`[Import/Direct] Worker ${workerId} — study ${studyIdx + 1} failed to parse: ${lastError}`);
                }
            }
            catch (error) {
                if (signal.aborted)
                    return;
                // Defensive: the settle poll retries transient failures internally, so
                // this is unexpected — surface the uploads as "still processing"
                // warnings rather than failures (the bytes uploaded and the backend
                // keeps parsing regardless).
                console.warn(`[Import/Direct] Worker ${workerId} — study ${studyIdx + 1} settle poll failed:`, error);
                const parsed = buildParseResults(studyUploadedIds, studyIdToFileName, new Map());
                aggregatedResult.totalFiles += parsed.results.length + studySkipped.length;
                aggregatedResult.warningCount += parsed.warningCount + studySkipped.length;
                aggregatedResult.results.push(...parsed.results, ...studySkipped);
            }
            // Account for anything still non-terminal at the cap plus skipped files.
            completedFiles += studyUploadedIds.length - studySettled + studySkipped.length;
            setState((prev) => ({
                ...prev,
                completedFiles,
                result: { ...aggregatedResult },
            }));
            if (studyAborted || studyParseFailed)
                failedStudies++;
            completedStudies++;
            setState((prev) => ({ ...prev, completedStudies }));
        }
    }
    const workerCount = Math.min(CONCURRENCY, Math.max(studies.length, 1));
    const workers = Array.from({ length: workerCount }, (_, i) => worker(i));
    await Promise.all(workers);
    byteProgress.flush();
    await finishImport(aggregatedResult, failedStudies, lastError, abortController.signal.aborted, setState, queryClient, clinicId, displayByUid, recordRecentImports);
}
